import { useState } from "react";
import { Link } from "react-router-dom";
import type { topics } from "@/mocks/home";

type Topic = (typeof topics)[number];

export default function TopicHeader({ topic }: { topic: Topic }) {
  const [votes, setVotes] = useState({ a: topic.votesA, b: topic.votesB, picked: null as "a" | "b" | null });

  const total = votes.a + votes.b;
  const percentA = Math.round((votes.a / total) * 100);

  const pick = (side: "a" | "b") => {
    setVotes((current) => {
      if (current.picked === side) return current;
      return {
        a: current.a + (side === "a" ? 1 : 0) - (current.picked === "a" ? 1 : 0),
        b: current.b + (side === "b" ? 1 : 0) - (current.picked === "b" ? 1 : 0),
        picked: side,
      };
    });
  };

  return (
    <header className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1400px] px-4 pt-6 md:px-8">
        <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
          <Link to="/#topics" className="transition-colors hover:text-primary-600">
            瓜田热榜
          </Link>
          <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
          <span className="text-foreground-700">{topic.categoryName}</span>
        </nav>
      </div>

      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-8 px-4 py-8 md:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-bold text-primary-700">
              {topic.categoryName}
            </span>
            {topic.hot && (
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-800">
                <i className="ri-fire-fill text-xs" aria-hidden="true" />
                热搜话题
              </span>
            )}
          </div>

          <h1 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[42px]">
            {topic.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line text-base" aria-hidden="true" />
              {topic.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-base" aria-hidden="true" />
              {topic.time}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-base text-primary-500" aria-hidden="true" />
              热度 {topic.heat.toLocaleString()}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-chat-3-line text-base" aria-hidden="true" />
              {topic.commentCount} 条评论
            </span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {topic.tags.map((tag) => (
              <span key={tag} className="rounded-md bg-secondary-50 px-2.5 py-1 text-xs text-secondary-800">
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-7 h-[240px] w-full overflow-hidden rounded-lg md:h-[340px]">
            <img
              src={topic.cover}
              alt={`${topic.title} - 网爆黑料话题现场配图`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-7">
            <h2 className="font-heading text-xl font-bold text-foreground-950">这个话题在聊什么</h2>
            <p className="mt-3 text-[15px] leading-[1.9] text-foreground-700">{topic.summary}</p>
            <p className="mt-4 text-[15px] leading-[1.9] text-foreground-700">
              目前的公开信息并不完整，正因如此，讨论才更需要克制。我们把已经能确认的部分整理在上方，
              其余内容一律标注为「存疑」。你可以先看看双方最有代表性的说法，再决定自己站哪一边。
            </p>
          </div>
        </div>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
            <h2 className="font-heading text-lg font-black text-foreground-950">站队投票</h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              已站队 <span className="font-bold text-primary-600">{total.toLocaleString()}</span> 人
            </p>

            <div className="mt-5 space-y-3">
              <button
                type="button"
                onClick={() => pick("a")}
                aria-pressed={votes.picked === "a"}
                className={`flex w-full items-center justify-between gap-3 rounded-md border px-4 py-3.5 text-left transition-colors ${
                  votes.picked === "a"
                    ? "border-primary-400 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-900 hover:border-primary-300"
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-bold">
                  <i className="ri-checkbox-circle-line text-lg" aria-hidden="true" />
                  {topic.sideA}
                </span>
                <span className="font-heading text-lg font-black">{percentA}%</span>
              </button>

              <button
                type="button"
                onClick={() => pick("b")}
                aria-pressed={votes.picked === "b"}
                className={`flex w-full items-center justify-between gap-3 rounded-md border px-4 py-3.5 text-left transition-colors ${
                  votes.picked === "b"
                    ? "border-secondary-500 bg-secondary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-900 hover:border-secondary-300"
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-bold">
                  <i className="ri-checkbox-circle-line text-lg" aria-hidden="true" />
                  {topic.sideB}
                </span>
                <span className="font-heading text-lg font-black">{100 - percentA}%</span>
              </button>
            </div>

            <div className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-background-200">
              <span className="h-full bg-primary-500" style={{ width: `${percentA}%` }} />
              <span className="h-full bg-secondary-400" style={{ width: `${100 - percentA}%` }} />
            </div>

            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mt-0.5 text-sm" aria-hidden="true" />
              投票结果仅代表参与者的即时选择，不构成任何事实认定。请理性表达，避免人身攻击。
            </p>
          </div>
        </aside>
      </div>
    </header>
  );
}